/**
 * 存货 / 采购调整自动伴生 line 规则（与 adjustDraftStore.buildSubmitHeader、AdjSheetVOBuilder 一致）
 * - BS-CA-INV 发起 → 自动追加 PL-COT-PUR、PL-COT-CI（仅 PL 勾稽，UI/导出/AFS 不展示）
 * - PL-COT-PUR 发起 → 自动追加 PL-COT-COS
 */

export const INV_COMPANION_CODES = ['PL-COT-PUR', 'PL-COT-CI'] as const;
export const PUR_COMPANION_CODES = ['PL-COT-COS'] as const;

/** Inventories WP 触发/取数主 code；FsSelectionDrawer 选 PL-COT-OI/CI 作 toPaper 时归一至此 */
export const INV_ADJUSTMENT_CODE = 'BS-CA-INV';
const INV_TO_PAPER_ALIAS_CODES = new Set(['PL-COT-OI', 'PL-COT-CI']);

export function normalizeFsCode(code: string | null | undefined): string {
    const trimmed = String(code || '').trim();
    return trimmed ? trimmed.toUpperCase() : '';
}

/**
 * WP_TO_WP toPaper：PL-COT-OI / PL-COT-CI → BS-CA-INV。
 * 仅用于 FsSelectionDrawer / useHandleFsSelection 的 WP→WP 选中路径；
 * WP_TO_FS、loadFromHeader、伴生 line 等勿调用。
 */
export function resolveInventoriesToPaperCode(code: string | null | undefined): string {
    const c = normalizeFsCode(code);
    if (!c) return '';
    return INV_TO_PAPER_ALIAS_CODES.has(c) ? INV_ADJUSTMENT_CODE : c;
}

export function collectLineCodes(
    lines: Array<{ code?: string; scheduleCode?: string }> | null | undefined,
    extraCodes: Array<string | null | undefined> = [],
): Set<string> {
    const codes = new Set<string>();
    for (const line of lines || []) {
        const c = normalizeFsCode(line?.code || line?.scheduleCode);
        if (c) codes.add(c);
    }
    for (const raw of extraCodes) {
        const c = normalizeFsCode(raw);
        if (c) codes.add(c);
    }
    return codes;
}

/** 根据 header 内 code 组合推断调整发起来源（不依赖 lines[0] 顺序） */
export function resolveAdjustmentOrigin(codes: Set<string>): 'BS-CA-INV' | 'PL-COT-PUR' | null {
    if (codes.has('BS-CA-INV')) return 'BS-CA-INV';
    if (codes.has('PL-COT-PUR')) return 'PL-COT-PUR';
    return null;
}

/** 导出 / UI 中应隐藏的伴生 code */
export function resolveCompanionHiddenCodes(codes: Set<string>): Set<string> {
    const hidden = new Set<string>();
    const origin = resolveAdjustmentOrigin(codes);
    if (origin === 'BS-CA-INV') {
        INV_COMPANION_CODES.forEach((c) => hidden.add(c));
    } else if (origin === 'PL-COT-PUR') {
        PUR_COMPANION_CODES.forEach((c) => hidden.add(c));
    }
    return hidden;
}

/**
 * BS-CA-INV 显式选择 PL-COT-PUR 作为 toPaper（WP to WP）。
 * 仅此组合下 Purchases 不作为伴生 hidden code；PL-COT-CI 仍隐藏。
 */
export function isInvExplicitPurchasesToPaper(
    fromCode: string | null | undefined,
    toPaperCodes: string[] | null | undefined,
): boolean {
    if (normalizeFsCode(fromCode) !== 'BS-CA-INV') return false;
    return (toPaperCodes || []).some((c) => normalizeFsCode(c) === 'PL-COT-PUR');
}

/**
 * PL-COT-PUR 显式选择 BS-CA-INV 作为 toPaper（WP to WP）。
 * 仅用于 AdjustTabsDialog 隐藏 Append Another Record。
 */
export function isPurExplicitInventoriesToPaper(
    fromCode: string | null | undefined,
    toPaperCodes: string[] | null | undefined,
): boolean {
    if (normalizeFsCode(fromCode) !== 'PL-COT-PUR') return false;
    return (toPaperCodes || []).some((c) => normalizeFsCode(c) === 'BS-CA-INV');
}

/** 后端回显：header.lines 有且仅有 INV / PUR / CI 三节点 */
const INV_PUR_CI_ONLY_CODES = new Set(['BS-CA-INV', 'PL-COT-PUR', 'PL-COT-CI']);

export function isInvPurchasesClosingInventoriesOnlyCodes(
    codes: Set<string> | null | undefined,
): boolean {
    if (!codes || codes.size !== INV_PUR_CI_ONLY_CODES.size) return false;
    for (const c of INV_PUR_CI_ONLY_CODES) {
        if (!codes.has(c)) return false;
    }
    return true;
}

/**
 * AdjustTabsDialog：下列 INV/PUR 配对场景永不渲染 Append Another Record。
 * - from INV 且 toPaper 含 PUR
 * - from PUR 且 toPaper 含 INV
 * - 后端回显且 header.lines 有且仅有 INV / PUR / CI
 */
export function shouldHideAppendAnotherRecordForInvPurchases(args: {
    fromCode: string | null | undefined;
    toPaperCodes: string[] | null | undefined;
    headerLines?: Array<{ code?: string; scheduleCode?: string }> | null;
    draftHydratedFromBackend?: boolean;
}): boolean {
    if (isInvExplicitPurchasesToPaper(args.fromCode, args.toPaperCodes)) return true;
    if (isPurExplicitInventoriesToPaper(args.fromCode, args.toPaperCodes)) return true;
    if (args.draftHydratedFromBackend) {
        return isInvPurchasesClosingInventoriesOnlyCodes(collectLineCodes(args.headerLines));
    }
    return false;
}

function unwrapHeaderLineDetail(details: unknown): Record<string, unknown> | null {
    if (!details || !Array.isArray(details) || details.length === 0) return null;
    let d: unknown = details[0];
    if (d && typeof d === 'object' && !Array.isArray(d) && (d as { payload?: unknown }).payload
        && typeof (d as { payload?: unknown }).payload === 'object') {
        d = (d as { payload: unknown }).payload;
    }
    return d && typeof d === 'object' && !Array.isArray(d)
        ? d as Record<string, unknown>
        : null;
}

/**
 * 后端 header.lines：INV 发起且 PL-COT-PUR 含 purchases 底稿（非伴生空 details）。
 * 仅用于 loadFromHeader 识别显式 INV→PUR 已保存记录。
 *
 * 缩窄：仅当 header 同时含 PL-COT-PUR、BS-CA-INV、PL-COT-CI 以及至少一个其他业务节点时，
 * 不视为显式 INV→PUR（PUR/CI 为 PL 勾稽伴生，Tabs 只渲染 INV 与其他节点）。
 * 其余场景（仅 INV+PUR+CI、新建 toPaper 选 PUR 等）保持原行为。
 */
export function isInvExplicitPurchasesHeaderLines(
    lines: Array<{ code?: string; scheduleCode?: string; details?: unknown }> | null | undefined,
): boolean {
    if (!lines?.length) return false;
    const codes = collectLineCodes(lines);
    if (resolveAdjustmentOrigin(codes) !== 'BS-CA-INV') return false;
    // INV+PUR+CI+其他节点：伴生勾稽，非显式 INV→PUR
    if (
        codes.has('BS-CA-INV')
        && codes.has('PL-COT-PUR')
        && codes.has('PL-COT-CI')
        && [...codes].some((c) => !INV_PUR_CI_ONLY_CODES.has(c))
    ) {
        return false;
    }
    const purLine = lines.find(
        (l) => normalizeFsCode(l.code || l.scheduleCode) === 'PL-COT-PUR',
    );
    if (!purLine) return false;
    const detail = unwrapHeaderLineDetail(purLine.details);
    const purchases = detail?.purchases;
    return Array.isArray(purchases) && purchases.length > 0;
}

/**
 * 仅 BS-CA-INV → PL-COT-PUR 显式配对：UI 伴生 hidden 集合中保留 PL-COT-PUR。
 * 调用方限定为 AdjustTabsDialog 与 adjustDraftStore 内部 Dr/Cr payload 组装。
 */
export function resolveInvPurchasesCompanionHiddenForUi(
    codes: Set<string>,
    fromCode: string | null | undefined,
    toPaperCodes: string[] | null | undefined,
): Set<string> {
    const hidden = resolveCompanionHiddenCodes(codes);
    if (isInvExplicitPurchasesToPaper(fromCode, toPaperCodes)) {
        hidden.delete('PL-COT-PUR');
    }
    return hidden;
}

export function isCompanionHiddenCode(code: string | null | undefined, hiddenCodes: Set<string>): boolean {
    const c = normalizeFsCode(code);
    return c !== '' && hiddenCodes.has(c);
}
